import * as Sentry from '@sentry/react';
import type { ViewerManifest, ViewerManifestResponse } from '../types/viewerDeployment';
import type { ViewerRouteContext } from '../utils/viewerRouteContext';
import { posthog } from '../lib/posthog';
import { buildApiUrl } from './apiUrl.js';
import { authenticatedFetch } from '../lib/apiClient';

function buildManifestUrl(route: ViewerRouteContext): string {
  const base = `/api/viewer-manifest/${route.kind}/${route.companyId}/${route.productId}`;
  if (route.kind === 'preview' && route.previewToken) {
    return buildApiUrl(`${base}?token=${encodeURIComponent(route.previewToken)}`);
  }

  return buildApiUrl(base);
}

function getManifestCacheMode(route: ViewerRouteContext): RequestCache {
  if (route.kind === 'admin' || route.kind === 'preview') {
    return 'no-store';
  }

  return 'default';
}

export async function fetchViewerManifest(route: ViewerRouteContext): Promise<ViewerManifest> {
  Sentry.addBreadcrumb({
    category: 'viewer.manifest',
    message: 'Fetching viewer manifest',
    data: { kind: route.kind, companyId: route.companyId, productId: route.productId },
  });

  try {
    const response = await authenticatedFetch(buildManifestUrl(route), {
      method: 'GET',
      cache: getManifestCacheMode(route),
    });

    // Don't use readAdminApiJsonOrThrow here — it dispatches admin-session-expired
    // on 401, which causes a redirect to /admin/sign-in from the viewer context.
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message = body?.error?.message || `Manifest fetch failed (${response.status})`;
      throw new Error(message);
    }

    const payload = await response.json() as ViewerManifestResponse;
    if (!payload?.data) {
      throw new Error(`Failed to load ${route.kind} manifest.`);
    }

    posthog.capture('viewer_session_started', { kind: route.kind, company_id: route.companyId, product_id: route.productId });
    return payload.data;
  } catch (err) {
    posthog.capture('viewer_manifest_fetch_failed', {
      kind: route.kind,
      company_id: route.companyId,
      product_id: route.productId,
      error: err instanceof Error ? err.message : String(err),
      error_type: err?.constructor?.name ?? 'Unknown',
    });
    Sentry.addBreadcrumb({
      category: 'viewer.manifest',
      level: 'error',
      message: 'Manifest fetch failed',
      data: {
        kind: route.kind,
        companyId: route.companyId,
        productId: route.productId,
        error: err instanceof Error ? err.message : String(err),
      },
    });
    throw err;
  }
}
